import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useUserSuppliesSectionStyle } from './UserSuppliesSection.style';
import { UserSuppliesRows } from './UserSuppliesRows';

import { Store } from '@/shared/store';
import { Vault } from '@/shared/api/vault';
import { EmptyBanner } from '@/widgets/Banners/EmptyBanner';
import { ConnectWalletBanner } from '@/widgets/Banners';

interface UserSuppliesSectionProps {
    title?: string;
    vault: Vault;
}

export const UserSuppliesSection: FC<UserSuppliesSectionProps> = ({
    title = 'Your Supplies',
    vault,
}) => {
    const classes = useUserSuppliesSectionStyle();

    return (
        <div className={classes.root}>
            <div className={classes.title}>{title}</div>
            <UserSuppliesRowWrapper vault={vault} />
        </div>
    );
};

type UserSuppliesRowWrapperProps = {
    vault: Vault;
};

export const UserSuppliesRowWrapper: FC<UserSuppliesRowWrapperProps> = ({ vault }) => {
    const classes = useUserSuppliesSectionStyle();

    const { walletAddress, vaultLinkedPools, linkedPoolsLoading } = useSelector((state: Store) => ({
        walletAddress: state.wallets.active.address,
        vaultLinkedPools: state.vaults.linkedPools,
        linkedPoolsLoading: state.vaults.linkedPoolsLoading,
    }));

    const linkedPools = useMemo(() => {
        const pools = vaultLinkedPools[vault?.address];

        return pools?.filter((pool) => Number(pool?.totalPosition) > 0) || [];
    }, [vaultLinkedPools, vault?.address]);

    if (!walletAddress) {
        return <ConnectWalletBanner />;
    }

    const notLoading = !linkedPoolsLoading && !linkedPools?.length;
    const userDontHaveSupplies = linkedPoolsLoading && linkedPools?.length === 0;

    if (notLoading || userDontHaveSupplies) {
        return <EmptyBanner />;
    }

    return (
        <div className={classes.tableWrapper}>
            <table className={classes.table}>
                <thead>
                    <tr className={classes.tr}>
                        <th>Pool</th>
                        <th>Asset</th>
                        <th>Your Balance</th>
                        <th>APY</th>
                        <th>Interest Earned</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    <UserSuppliesRows vault={vault} items={linkedPools} />
                </tbody>
            </table>
        </div>
    );
};
